/*
 * Valhalla Tactical — in-store pickup: header store selector + PDP "Buy Online" block.
 * Ported from Mastermind Toys (store-selector.js + buy-online-options.js).
 *
 * Differences from Mastermind:
 *  - Mastermind reads stores/stock from the Inventory Info app (window.inventoryInfo,
 *    rest.locationinventory.info). Valhalla doesn't have that app, so the store list comes from
 *    the "Store selector bar" section blocks (window.vtStores) and pickup availability from
 *    Shopify's native variant.store_availabilities.
 *  - No Google Places autocomplete (no API key on Valhalla): plain text search + "Use my location".
 *  - No Storefront API checkout prefill (needs a storefront token; Shopify checkout already offers
 *    pickup for pickup-enabled locations).
 *
 * Persistence is the same as Mastermind:
 *  - localStorage['storeSelectorSelection'] = { id, label }
 *  - cart attributes pickup_location_id / pickup_location_name / pickup_mode (explicit actions only)
 *  - document event 'store-selector:change' with detail { id, label }
 *
 * Included by both the header section and the PDP snippet, so it guards against double execution.
 */
(function () {
  'use strict';

  if (window.VTStoreSelector) return;

  var STORAGE_KEY = 'storeSelectorSelection';
  var MODE_KEY = 'storeSelectorMode';
  var CHANGE_EVENT = 'store-selector:change';
  var FOCUSABLE =
    'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  /* ---------- helpers ---------- */

  function storageGet(key) {
    try {
      return window.localStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  function storageSet(key, value) {
    try {
      if (value === null) window.localStorage.removeItem(key);
      else window.localStorage.setItem(key, value);
    } catch (e) {
      /* private mode / blocked storage */
    }
  }

  function splitList(value) {
    return String(value || '')
      .split(',')
      .map(normalize)
      .filter(Boolean);
  }

  function toNumber(value) {
    var num = parseFloat(value);
    return isFinite(num) ? num : null;
  }

  function normalize(value) {
    return String(value || '').trim().toLowerCase();
  }

  function h(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined && text !== null && text !== '') node.textContent = text;
    return node;
  }

  function parseJsonScript(script) {
    if (!script) return null;
    try {
      var data = JSON.parse(script.textContent);
      if (data && !Array.isArray(data.locations)) data.locations = [];
      return data;
    } catch (e) {
      console.warn('[VTStoreSelector] Invalid availability JSON', e);
      return null;
    }
  }

  function storeAddressLine(store) {
    var cityLine = [store.city, [store.region, store.postcode].filter(Boolean).join(' ')].filter(Boolean).join(', ');
    return [store.address1, cityLine].filter(Boolean).join(', ');
  }

  function matchesQuery(store, query) {
    if (!query) return true;
    var haystack = normalize([store.name, store.address1, store.city, store.region, store.postcode].join(' '));
    return normalize(query)
      .split(/\s+/)
      .every(function (term) {
        return haystack.indexOf(term) !== -1;
      });
  }

  function trapFocus(container, event) {
    var focusable = Array.prototype.slice.call(container.querySelectorAll(FOCUSABLE)).filter(function (el) {
      return el.offsetParent !== null || el === document.activeElement;
    });
    if (!focusable.length) {
      event.preventDefault();
      return;
    }
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /* ---------- distance ---------- */

  var userCoords = null;
  var coordsPromise = null;

  function distanceKm(store) {
    if (!userCoords || store.lat === null || store.lng === null) return Infinity;
    var toRad = function (deg) {
      return (deg * Math.PI) / 180;
    };
    var dLat = toRad(store.lat - userCoords.lat);
    var dLng = toRad(store.lng - userCoords.lng);
    var a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(userCoords.lat)) * Math.cos(toRad(store.lat)) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
    return 6371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  function formatDistance(km) {
    if (!isFinite(km)) return '';
    if (km < 1) return Math.round(km * 1000) + ' m';
    if (km < 10) return km.toFixed(1) + ' km';
    return Math.round(km) + ' km';
  }

  function sortByDistance(stores) {
    if (!userCoords) return stores;
    return stores
      .map(function (store, index) {
        return { store: store, distance: distanceKm(store), index: index };
      })
      .sort(function (a, b) {
        if (a.distance === b.distance) return a.index - b.index;
        return a.distance - b.distance;
      })
      .map(function (entry) {
        return entry.store;
      });
  }

  function requestUserCoords() {
    if (userCoords) return Promise.resolve(userCoords);
    if (coordsPromise) return coordsPromise;
    if (!navigator.geolocation) return Promise.reject(new Error('Geolocation not supported'));
    coordsPromise = new Promise(function (resolve, reject) {
      navigator.geolocation.getCurrentPosition(
        function (position) {
          userCoords = { lat: position.coords.latitude, lng: position.coords.longitude };
          resolve(userCoords);
        },
        function (error) {
          coordsPromise = null;
          reject(error);
        },
        { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 }
      );
    });
    return coordsPromise;
  }

  /* ---------- store list + selection ---------- */

  function getStores() {
    var list = Array.isArray(window.vtStores) ? window.vtStores : [];
    return list
      .filter(function (store) {
        return store && store.id && store.name;
      })
      .map(function (store) {
        return {
          id: String(store.id),
          name: store.name,
          address1: store.address1 || '',
          city: store.city || '',
          region: store.region || '',
          postcode: store.postcode || '',
          phone: store.phone || '',
          lat: toNumber(store.lat),
          lng: toNumber(store.lng),
          locationName: store.locationName || '',
        };
      });
  }

  function getSelection() {
    var raw = storageGet(STORAGE_KEY);
    if (!raw) return null;
    try {
      var parsed = JSON.parse(raw);
      if (parsed && parsed.id) return { id: String(parsed.id), label: parsed.label || '' };
    } catch (e) {
      /* ignore */
    }
    return null;
  }

  function findStore(stores, id) {
    if (!id) return null;
    for (var i = 0; i < stores.length; i++) {
      if (stores[i].id === String(id)) return stores[i];
    }
    return null;
  }

  function emitChange(store) {
    document.dispatchEvent(
      new CustomEvent(CHANGE_EVENT, {
        detail: { id: store ? store.id : '', label: store ? store.name : '' },
      })
    );
  }

  function selectStore(store) {
    if (!store) {
      clearStore();
      return;
    }
    storageSet(STORAGE_KEY, JSON.stringify({ id: store.id, label: store.name }));
    queueCartAttributes({ pickup_location_id: store.id, pickup_location_name: store.name });
    emitChange(store);
  }

  function clearStore() {
    storageSet(STORAGE_KEY, null);
    queueCartAttributes({ pickup_location_id: '', pickup_location_name: '' });
    emitChange(null);
  }

  function getMode() {
    var mode = storageGet(MODE_KEY);
    return mode === 'pickup' || mode === 'ship' ? mode : '';
  }

  function setMode(mode) {
    storageSet(MODE_KEY, mode);
    queueCartAttributes({ pickup_mode: mode });
  }

  /* ---------- per-location stock from Fast Simon (Ship to Home) ---------- */

  // Shopify's theme data only knows pickup-enabled locations, so stock in the online warehouse
  // ("Valhalla Warehouse", shown as "Online" in the filter) comes from the Fast Simon index: each
  // variant carries a "Locations" attribute listing the locations that stock it.
  // This is the request FastSimonSDK.getProducts({ direct: true, withAttributes, withProductVariants })
  // makes. It is called directly because loading + initializing the SDK on the PDP also fires a
  // post_load analytics ping, /load and /pop requests. One request per product, shared by every variant.
  var FS_PRODUCTS_URL = 'https://api.fastsimon.com/search/get_format_products';
  var FS_TIMEOUT = 3000;
  var fsLocationsByProduct = {};

  // Resolves to { variantId: ["Valhalla Warehouse", "Brisbane Store", ...] } or null when unavailable.
  function getFastSimonLocations(productId, storeId, uuid) {
    if (!productId || !storeId || !uuid) return Promise.resolve(null);
    if (fsLocationsByProduct[productId]) return fsLocationsByProduct[productId];
    var url =
      FS_PRODUCTS_URL +
      '?UUID=' + encodeURIComponent(uuid) +
      '&store_id=' + encodeURIComponent(storeId) +
      '&products=' + encodeURIComponent(productId) +
      '&with_product_attributes=true&with_product_variants=true';
    var request = fetch(url)
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.json();
      })
      .then(function (json) {
        var product = json && Array.isArray(json.items) ? json.items[0] : null;
        if (!product || !Array.isArray(product.vra)) return null;
        var map = {};
        product.vra.forEach(function (entry) {
          var attributes = Array.isArray(entry && entry[1]) ? entry[1] : [];
          for (var i = 0; i < attributes.length; i++) {
            if (attributes[i] && attributes[i][0] === 'Locations' && Array.isArray(attributes[i][1])) {
              map[String(entry[0])] = attributes[i][1].map(String);
              return;
            }
          }
        });
        return map;
      });
    var timeout = new Promise(function (resolve) {
      setTimeout(resolve, FS_TIMEOUT, null);
    });
    fsLocationsByProduct[productId] = Promise.race([request, timeout]).catch(function (error) {
      console.warn('[VTBuyOnline] Fast Simon locations unavailable', error);
      return null;
    });
    return fsLocationsByProduct[productId];
  }

  /* ---------- cart attributes (debounced, explicit actions only) ---------- */

  var pendingAttributes = null;
  var attributesTimer = null;
  var sentAttributes = {};

  function queueCartAttributes(attributes) {
    var settings = window.vtStoreSettings || {};
    if (settings.saveToCart === false) return;
    pendingAttributes = Object.assign(pendingAttributes || {}, attributes);
    clearTimeout(attributesTimer);
    attributesTimer = setTimeout(flushCartAttributes, 500);
  }

  function flushCartAttributes() {
    var attributes = pendingAttributes;
    pendingAttributes = null;
    if (!attributes) return;

    var changed = {};
    var hasChanges = false;
    Object.keys(attributes).forEach(function (key) {
      if (sentAttributes[key] !== attributes[key]) {
        changed[key] = attributes[key];
        hasChanges = true;
      }
    });
    if (!hasChanges) return;

    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    fetch(root + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: changed }),
    })
      .then(function (response) {
        if (response.ok) Object.assign(sentAttributes, changed);
      })
      .catch(function (error) {
        console.warn('[VTStoreSelector] Failed to update cart attributes', error);
      });
  }

  window.VTStoreSelector = {
    getStores: getStores,
    getSelection: getSelection,
    getSelectedStore: function () {
      var selection = getSelection();
      return selection ? findStore(getStores(), selection.id) : null;
    },
    selectStore: selectStore,
    clearStore: clearStore,
    getMode: getMode,
    setMode: setMode,
  };

  /* ======================================================================
   * <vt-store-selector> — header bar + "Choose your store" modal
   * ==================================================================== */

  class VTStoreSelectorElement extends HTMLElement {
    connectedCallback() {
      this.trigger = this.querySelector('[data-vt-store-open]');
      this.valueEl = this.querySelector('[data-vt-store-value]');
      this.modal = this.querySelector('[data-vt-store-modal]') || this.modal;
      if (!this.trigger || !this.modal) return;

      // Mastermind moves the modal to <body> so header stacking contexts/overflow can't clip it.
      if (this.modal.parentNode !== document.body) document.body.appendChild(this.modal);

      this.dialog = this.modal.querySelector('[role="dialog"]');
      this.optionsList = this.modal.querySelector('[data-vt-store-options]');
      this.searchInput = this.modal.querySelector('[data-vt-store-search]');
      this.useLocationButton = this.modal.querySelector('[data-vt-store-use-location]');
      this.clearButton = this.modal.querySelector('[data-vt-store-clear]');
      this.statusEl = this.modal.querySelector('[data-vt-store-status]');
      this.placeholder = this.dataset.placeholder || 'Pick Up In Store - Choose Your Location!';
      this.selectedText = this.dataset.selectedText || '[store]';

      this.onTriggerClick = this.open.bind(this);
      this.onModalClick = this.handleModalClick.bind(this);
      this.onSearch = this.renderOptions.bind(this);
      this.onUseLocation = this.useLocation.bind(this);
      this.onClear = this.handleClear.bind(this);
      this.onKeydown = this.handleKeydown.bind(this);
      this.onSelectionChange = this.syncFromSelection.bind(this);

      this.trigger.addEventListener('click', this.onTriggerClick);
      this.modal.addEventListener('click', this.onModalClick);
      this.searchInput && this.searchInput.addEventListener('input', this.onSearch);
      this.useLocationButton && this.useLocationButton.addEventListener('click', this.onUseLocation);
      this.clearButton && this.clearButton.addEventListener('click', this.onClear);
      document.addEventListener(CHANGE_EVENT, this.onSelectionChange);

      this.syncFromSelection();
    }

    disconnectedCallback() {
      if (!this.trigger || !this.modal) return;
      this.trigger.removeEventListener('click', this.onTriggerClick);
      this.modal.removeEventListener('click', this.onModalClick);
      this.searchInput && this.searchInput.removeEventListener('input', this.onSearch);
      this.useLocationButton && this.useLocationButton.removeEventListener('click', this.onUseLocation);
      this.clearButton && this.clearButton.removeEventListener('click', this.onClear);
      document.removeEventListener(CHANGE_EVENT, this.onSelectionChange);
      document.removeEventListener('keydown', this.onKeydown);
      document.documentElement.classList.remove('store-selector-open');
      if (this.modal.parentNode) this.modal.parentNode.removeChild(this.modal);
    }

    syncFromSelection() {
      var store = window.VTStoreSelector.getSelectedStore();
      if (this.valueEl) {
        this.valueEl.textContent = store ? this.selectedText.replace('[store]', store.name) : this.placeholder;
      }
      if (this.clearButton) this.clearButton.disabled = !store;
      if (!this.modal.hidden) this.renderOptions();
    }

    renderOptions() {
      if (!this.optionsList) return;
      var query = this.searchInput ? this.searchInput.value.trim() : '';
      var selected = window.VTStoreSelector.getSelectedStore();
      var stores = sortByDistance(
        getStores().filter(function (store) {
          return matchesQuery(store, query);
        })
      );

      var fragment = document.createDocumentFragment();
      stores.forEach(function (store) {
        var isActive = Boolean(selected && selected.id === store.id);
        var item = h('li');
        var button = h('button', 'store-selector__option' + (isActive ? ' is-active' : ''));
        button.type = 'button';
        button.setAttribute('data-vt-store-option', '');
        button.dataset.id = store.id;
        button.setAttribute('aria-pressed', isActive ? 'true' : 'false');

        var label = h('span', 'store-selector__option-label');
        label.appendChild(h('span', 'store-selector__option-name', store.name));
        var distance = formatDistance(distanceKm(store));
        if (distance) label.appendChild(h('span', 'store-selector__option-distance', distance));
        button.appendChild(label);

        var address = storeAddressLine(store);
        if (address) button.appendChild(h('span', 'store-selector__option-address', address));
        if (isActive) button.appendChild(h('span', 'store-selector__option-state', 'Selected'));

        item.appendChild(button);
        fragment.appendChild(item);
      });

      this.optionsList.innerHTML = '';
      this.optionsList.appendChild(fragment);
      this.setStatus(stores.length ? '' : query ? 'No stores match your search.' : 'No stores are available right now.');
    }

    handleModalClick(event) {
      if (event.target.closest('[data-vt-store-close]')) {
        event.preventDefault();
        this.close();
        return;
      }
      var option = event.target.closest('[data-vt-store-option]');
      if (!option) return;
      var store = findStore(getStores(), option.dataset.id);
      if (!store) return;
      // Same as Mastermind: clicking the active store clears the selection.
      if (option.getAttribute('aria-pressed') === 'true') window.VTStoreSelector.clearStore();
      else window.VTStoreSelector.selectStore(store);
      this.close();
    }

    handleClear(event) {
      event.preventDefault();
      window.VTStoreSelector.clearStore();
      this.close();
    }

    useLocation() {
      var self = this;
      this.setStatus('Finding your location…');
      requestUserCoords()
        .then(function () {
          self.setStatus('');
          self.renderOptions();
        })
        .catch(function () {
          self.setStatus("We couldn't get your location. Search by suburb or postcode instead.");
        });
    }

    setStatus(text) {
      if (!this.statusEl) return;
      this.statusEl.textContent = text || '';
      this.statusEl.hidden = !text;
    }

    open() {
      this.renderOptions();
      this.modal.hidden = false;
      this.trigger.setAttribute('aria-expanded', 'true');
      document.documentElement.classList.add('store-selector-open');
      document.addEventListener('keydown', this.onKeydown);
      (this.searchInput || this.dialog).focus();
    }

    close() {
      if (this.modal.hidden) return;
      this.modal.hidden = true;
      this.trigger.setAttribute('aria-expanded', 'false');
      document.documentElement.classList.remove('store-selector-open');
      document.removeEventListener('keydown', this.onKeydown);
      this.trigger.focus();
    }

    handleKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
      } else if (event.key === 'Tab' && this.dialog) {
        trapFocus(this.dialog, event);
      }
    }
  }

  /* ======================================================================
   * <vt-buy-online> — PDP Ship to Home / Pick Up block + stores drawer
   * ==================================================================== */

  class VTBuyOnlineElement extends HTMLElement {
    connectedCallback() {
      if (!this.initialized) {
        this.initialized = true;
        this.cache = {};
        this.productUrl = this.dataset.productUrl || window.location.pathname;
        this.variantId = String(this.dataset.variantId || '');
        this.data = parseJsonScript(this.querySelector('script[data-vt-availability]'));
        if (this.data) this.cache[String(this.data.variantId)] = this.data;

        // Ship to Home = stock at an online warehouse (Fast Simon "Locations") or at a store that
        // also ships online (Shopify pickup data). Until Fast Simon answers, show "Checking…".
        this.onlineLocations = splitList(this.dataset.onlineLocations);
        this.onlineStores = splitList(this.dataset.onlineStores);
        this.fsLocations = undefined;
        var self = this;
        getFastSimonLocations(this.dataset.productId, this.dataset.fsStoreId, this.dataset.fsUuid).then(function (map) {
          self.fsLocations = map;
          self.render();
        });

        this.shipTab = this.querySelector('[data-bo-mode="ship"]');
        this.pickupTab = this.querySelector('[data-bo-mode="pickup"]');
        this.shipStatusEl = this.querySelector('[data-bo-ship-status]');
        this.pickupStatusEl = this.querySelector('[data-bo-pickup-status]');
        this.selectedRows = Array.prototype.slice.call(this.querySelectorAll('[data-bo-selected-row]'));
        this.selectedStoreEl = this.querySelector('[data-bo-selected-store]');
        this.stockWrapper = this.querySelector('.buy-online__stock');
        this.stockEl = this.querySelector('[data-bo-stock]');
        this.selectedTimeEl = this.querySelector('[data-bo-selected-time]');
        this.selectedAddressEl = this.querySelector('[data-bo-selected-address]');
        this.selectedPhoneEl = this.querySelector('[data-bo-selected-phone]');
        this.openButton = this.querySelector('[data-bo-open-drawer]');
        this.drawer = this.querySelector('[data-bo-drawer]');
        if (this.drawer) {
          this.drawerPanel = this.drawer.querySelector('[role="dialog"]');
          this.listEl = this.drawer.querySelector('[data-bo-list]');
          this.searchInput = this.drawer.querySelector('[data-bo-search]');
          this.nearbyButton = this.drawer.querySelector('[data-bo-nearby]');
        }

        // Saved mode counts as an explicit shopper choice (Mastermind restores it from cart attributes).
        this.mode = window.VTStoreSelector.getMode() || 'ship';
        this.userChoseMode = Boolean(window.VTStoreSelector.getMode());

        this.onTabClick = this.handleTabClick.bind(this);
        this.onOpenDrawer = this.openDrawer.bind(this);
        this.onDrawerClick = this.handleDrawerClick.bind(this);
        this.onSearch = this.renderList.bind(this);
        this.onNearby = this.useLocation.bind(this);
        this.onKeydown = this.handleKeydown.bind(this);
        this.onSelectionChange = this.render.bind(this);
        this.onVariantInput = this.handleVariantInput.bind(this);
      }

      // Mastermind moves the drawer to <body> so the sticky product column can't clip it.
      if (this.drawer && this.drawer.parentNode !== document.body) document.body.appendChild(this.drawer);

      this.shipTab && this.shipTab.addEventListener('click', this.onTabClick);
      this.pickupTab && this.pickupTab.addEventListener('click', this.onTabClick);
      this.openButton && this.openButton.addEventListener('click', this.onOpenDrawer);
      if (this.drawer) {
        this.drawer.addEventListener('click', this.onDrawerClick);
        this.searchInput && this.searchInput.addEventListener('input', this.onSearch);
        this.nearbyButton && this.nearbyButton.addEventListener('click', this.onNearby);
      }
      document.addEventListener(CHANGE_EVENT, this.onSelectionChange);

      // Taiga's variant picker sets form input[name="id"] and dispatches a bubbling "change".
      // Listen on this product section only (not the whole document).
      this.variantScope = this.closest('.shopify-section') || document;
      this.variantScope.addEventListener('change', this.onVariantInput);

      // The section may have re-rendered with a different variant than we last saw.
      var form = this.variantScope.querySelector('form[action*="/cart/add"] input[name="id"]');
      if (form && form.value && String(form.value) !== this.variantId) this.loadVariant(String(form.value));
      else this.render();
    }

    disconnectedCallback() {
      this.shipTab && this.shipTab.removeEventListener('click', this.onTabClick);
      this.pickupTab && this.pickupTab.removeEventListener('click', this.onTabClick);
      this.openButton && this.openButton.removeEventListener('click', this.onOpenDrawer);
      if (this.drawer) {
        this.drawer.removeEventListener('click', this.onDrawerClick);
        this.searchInput && this.searchInput.removeEventListener('input', this.onSearch);
        this.nearbyButton && this.nearbyButton.removeEventListener('click', this.onNearby);
        if (!this.drawer.hidden) this.closeDrawer();
        if (this.drawer.parentNode) this.drawer.parentNode.removeChild(this.drawer);
      }
      document.removeEventListener(CHANGE_EVENT, this.onSelectionChange);
      if (this.variantScope) this.variantScope.removeEventListener('change', this.onVariantInput);
      if (this.abortController) this.abortController.abort();
      clearTimeout(this.variantTimer);
    }

    /* ----- variant switching ----- */

    handleVariantInput(event) {
      var input = event.target;
      if (!input || input.name !== 'id' || !input.closest('form[action*="/cart/add"]')) return;
      var id = String(input.value || '');
      if (!id || id === this.variantId) return;
      var self = this;
      clearTimeout(this.variantTimer);
      this.variantTimer = setTimeout(function () {
        self.loadVariant(id);
      }, 150);
    }

    loadVariant(id) {
      var self = this;
      this.variantId = id;
      if (this.cache[id]) {
        this.data = this.cache[id];
        this.render();
        return;
      }

      this.data = null;
      this.render();

      if (this.abortController) this.abortController.abort();
      var controller = (this.abortController = new AbortController());
      var joiner = this.productUrl.indexOf('?') === -1 ? '?' : '&';
      var url = this.productUrl + joiner + 'variant=' + encodeURIComponent(id) + '&section_id=vt-store-availability';

      fetch(url, { signal: controller.signal })
        .then(function (response) {
          if (!response.ok) throw new Error('HTTP ' + response.status);
          return response.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var data = parseJsonScript(doc.querySelector('script[data-vt-availability]'));
          if (!data) throw new Error('No availability data');
          self.cache[id] = data;
          if (self.variantId === id) {
            self.data = data;
            self.render();
          }
        })
        .catch(function (error) {
          if (error && error.name === 'AbortError') return;
          console.warn('[VTBuyOnline] Failed to load pickup availability', error);
          if (self.variantId === id) {
            self.data = { variantId: id, available: null, locations: [], error: true };
            self.render();
          }
        });
    }

    /* ----- availability ----- */

    getStoreList() {
      var stores = getStores();
      if (stores.length) return stores;
      // Fallback when the "Store selector bar" section isn't installed: Shopify's pickup locations.
      return ((this.data && this.data.locations) || []).map(function (location) {
        return {
          id: String(location.id),
          name: location.name,
          address1: '',
          city: '',
          region: '',
          postcode: '',
          phone: '',
          lat: null,
          lng: null,
          locationName: location.name,
        };
      });
    }

    storeStatus(store) {
      var data = this.data;
      if (!data) return { state: 'loading', text: 'Checking…' };
      if (data.error) return { state: 'unknown', text: "Couldn't check this store" };
      var keys = store.locationName ? [normalize(store.locationName)] : [normalize(store.name), normalize(store.name + ' Store')], key = keys[0]; // blank location name -> "<name>" / "<name> Store"
      var location = null;
      if (key) {
        for (var i = 0; i < data.locations.length; i++) {
          if (keys.indexOf(normalize(data.locations[i].name)) !== -1) {
            location = data.locations[i];
            break;
          }
        }
      }
      if (!location || location.pickUpEnabled === false) {
        return { state: 'not-offered', text: 'In-store pickup not available at this store' };
      }
      if (location.available) {
        return { state: 'available', text: 'Available for pickup', time: String(location.pickUpTime || '').replace(/^\s*usually ready in\s*/i, '') }; // pick_up_time already says "Usually ready in"
      }
      return { state: 'unavailable', text: 'Out of stock for pickup' };
    }

    // true / false, or null while Fast Simon is still loading.
    shipStatus(data) {
      if (data.available !== true) return false;
      // Untracked or "continue selling" variants ship regardless of location stock (as before).
      if (!data.inventoryTracked || data.inventoryPolicy === 'continue') return true;
      if (this.fsLocations === undefined) return null;

      var self = this;
      var storeStock = (data.locations || []).filter(function (location) {
        return location.available;
      });
      var stockedAtOnlineStore = storeStock.some(function (location) {
        return self.onlineStores.indexOf(normalize(location.name)) !== -1;
      });
      if (stockedAtOnlineStore) return true;

      var fsLocations = this.fsLocations && this.fsLocations[String(data.variantId)];
      if (!fsLocations) return true; // no Fast Simon data: fall back to Shopify's "available"
      var fsKeys = fsLocations.map(normalize);
      // Fast Simon must agree with Shopify about the stores; if it misses one, its data is stale.
      var consistent = storeStock.every(function (location) {
        return fsKeys.indexOf(normalize(location.name)) !== -1;
      });
      if (!consistent) return true;
      return fsKeys.some(function (name) {
        return self.onlineLocations.indexOf(name) !== -1;
      });
    }

    render() {
      var self = this;
      var data = this.data;
      var loaded = Boolean(data && !data.error);
      var stores = this.getStoreList();
      var selection = getSelection();
      var selected = selection ? findStore(stores, selection.id) : null;
      var selectedStatus = selected ? this.storeStatus(selected) : null;
      var anyPickup = stores.some(function (store) {
        return self.storeStatus(store).state === 'available';
      });

      var ship = loaded ? this.shipStatus(data) : null;
      this.shipAvailable = ship === true;
      this.pickupAvailable = loaded && (selected ? selectedStatus.state === 'available' : anyPickup);

      // Ship to Home
      if (!data || ship === null) this.setStatus(this.shipStatusEl, 'loading', 'Checking…');
      else if (data.error) this.setStatus(this.shipStatusEl, 'unknown', "Couldn't check availability");
      else this.setStatus(this.shipStatusEl, this.shipAvailable ? 'ok' : 'unavailable', this.shipAvailable ? 'Available' : 'Unavailable');

      // Pick Up
      var pickupText;
      var pickupState;
      if (!data) {
        pickupState = 'loading';
        pickupText = 'Checking stores…';
      } else if (data.error) {
        pickupState = 'unknown';
        pickupText = "Couldn't check stores";
      } else if (selected) {
        pickupState = selectedStatus.state === 'available' ? 'ok' : 'unavailable';
        pickupText =
          selectedStatus.state === 'available'
            ? 'Available at ' + selected.name
            : selectedStatus.state === 'not-offered'
            ? 'Pickup not available at ' + selected.name
            : 'Unavailable at ' + selected.name;
      } else {
        pickupState = anyPickup ? 'ok' : 'unavailable';
        pickupText = anyPickup ? 'Available at select stores' : 'Unavailable';
      }
      this.setStatus(this.pickupStatusEl, pickupState, pickupText);

      // Mode: shopper preference, auto-switched (without saving) when the preferred mode is unavailable.
      var mode = this.mode;
      var shipKnown = loaded && ship !== null;
      if (shipKnown) {
        if (mode === 'ship' && !this.shipAvailable && this.pickupAvailable) mode = 'pickup';
        else if (mode === 'pickup' && !this.pickupAvailable && this.shipAvailable && !this.userChoseMode) mode = 'ship';
      }
      this.displayMode = mode;
      this.updateTab(this.shipTab, mode === 'ship', shipKnown && !this.shipAvailable);
      this.updateTab(this.pickupTab, mode === 'pickup', loaded && !this.pickupAvailable);

      // "Pick up at <store>" rows: only when a store is chosen and it has the item (same as Mastermind).
      var showSelected = Boolean(selected && selectedStatus.state === 'available');
      this.selectedRows.forEach(function (row) {
        row.hidden = !showSelected;
      });
      if (showSelected) {
        this.selectedStoreEl.textContent = selected.name;
        this.stockEl.textContent = 'In stock';
        this.stockWrapper.classList.add('is-available');
        this.selectedTimeEl.textContent = selectedStatus.time ? 'Usually ready in ' + selectedStatus.time : '';
        this.selectedAddressEl.textContent = storeAddressLine(selected);
        this.selectedPhoneEl.textContent = selected.phone || '';
      }

      if (this.openButton) {
        this.openButton.textContent = selected ? 'Check other stores' : 'Check store availability';
      }

      if (this.drawer && !this.drawer.hidden) this.renderList();
    }

    setStatus(target, state, text) {
      if (!target) return;
      target.classList.toggle('buy-online__status--ok', state === 'ok');
      target.classList.toggle('is-unavailable', state === 'unavailable');
      target.classList.toggle('is-loading', state === 'loading' || state === 'unknown');
      var label = target.querySelector('[data-bo-status-text]');
      if (label) label.textContent = text;
    }

    updateTab(tab, isActive, isUnavailable) {
      if (!tab) return;
      tab.classList.toggle('is-active', isActive);
      tab.setAttribute('aria-checked', isActive ? 'true' : 'false');
      tab.classList.toggle('is-unavailable', isUnavailable);
      tab.setAttribute('aria-disabled', isUnavailable ? 'true' : 'false');
    }

    handleTabClick(event) {
      var mode = event.currentTarget.getAttribute('data-bo-mode');
      if (mode === 'pickup' && !this.pickupAvailable) return;
      if (mode === 'ship' && !this.shipAvailable) return;
      if (mode === this.displayMode && this.userChoseMode) return;
      this.mode = mode;
      this.userChoseMode = true;
      window.VTStoreSelector.setMode(mode);
      this.render();
    }

    /* ----- drawer ----- */

    renderList() {
      if (!this.listEl) return;
      var self = this;
      var query = this.searchInput ? this.searchInput.value.trim() : '';
      var selection = getSelection();
      var stores = sortByDistance(
        this.getStoreList().filter(function (store) {
          return matchesQuery(store, query);
        })
      );

      this.listEl.innerHTML = '';
      if (!stores.length) {
        this.listEl.appendChild(h('div', 'bo-drawer__empty', query ? 'No stores found.' : 'No stores are available right now.'));
        return;
      }

      var fragment = document.createDocumentFragment();
      stores.forEach(function (store) {
        var status = self.storeStatus(store);
        var isSelected = Boolean(selection && selection.id === store.id);
        var item = h('article', 'bo-store' + (isSelected ? ' is-selected' : ''));
        item.appendChild(h('div', 'bo-store__title', store.name));

        var meta = h('div', 'bo-store__meta');
        var distance = formatDistance(distanceKm(store));
        if (distance) meta.appendChild(h('div', 'bo-store__distance', distance));
        if (store.address1) meta.appendChild(h('div', '', store.address1));
        var cityLine = [store.city, store.region, store.postcode].filter(Boolean).join(' ');
        if (cityLine) meta.appendChild(h('div', '', cityLine));
        if (store.phone) {
          var phoneRow = h('div');
          var phoneLink = h('a', 'bo-store__phone', store.phone);
          phoneLink.href = 'tel:' + store.phone.replace(/[^0-9+]/g, '');
          phoneRow.appendChild(phoneLink);
          meta.appendChild(phoneRow);
        }

        var stateClass =
          status.state === 'available' ? 'is-available' : status.state === 'unavailable' ? 'is-unavailable' : 'is-not-offered';
        var qty = h('div', 'bo-store__qty ' + stateClass);
        var dot = h('span', 'bo-store__dot');
        dot.setAttribute('aria-hidden', 'true');
        qty.appendChild(dot);
        qty.appendChild(h('span', '', status.time ? status.text + ' · usually ready in ' + status.time : status.text));
        meta.appendChild(qty);
        item.appendChild(meta);

        var actions = h('div', 'bo-store__actions');
        var button = h('button', 'bo-store__select');
        button.type = 'button';
        button.dataset.id = store.id;
        button.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
        var marker = h('span', 'bo-store__select-marker');
        marker.setAttribute('aria-hidden', 'true');
        button.appendChild(marker);
        button.appendChild(h('span', '', isSelected ? 'Your store' : 'Select this store'));
        actions.appendChild(button);
        item.appendChild(actions);

        fragment.appendChild(item);
      });
      this.listEl.appendChild(fragment);
    }

    handleDrawerClick(event) {
      if (event.target.closest('[data-bo-close]')) {
        event.preventDefault();
        this.closeDrawer();
        return;
      }
      var button = event.target.closest('.bo-store__select');
      if (!button) return;
      var store = findStore(this.getStoreList(), button.dataset.id);
      if (!store) return;
      window.VTStoreSelector.selectStore(store);
      this.closeDrawer();
    }

    useLocation() {
      var self = this;
      requestUserCoords()
        .then(function () {
          self.renderList();
        })
        .catch(function (error) {
          console.warn('[VTBuyOnline] Geolocation error', error);
        });
    }

    openDrawer() {
      if (!this.drawer) return;
      this.renderList();
      this.drawer.hidden = false;
      document.documentElement.classList.add('bo-drawer-open');
      document.addEventListener('keydown', this.onKeydown);
      (this.searchInput || this.drawerPanel).focus();
    }

    closeDrawer() {
      if (!this.drawer || this.drawer.hidden) return;
      this.drawer.hidden = true;
      document.documentElement.classList.remove('bo-drawer-open');
      document.removeEventListener('keydown', this.onKeydown);
      if (this.openButton && this.openButton.isConnected) this.openButton.focus();
    }

    handleKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeDrawer();
      } else if (event.key === 'Tab' && this.drawerPanel) {
        trapFocus(this.drawerPanel, event);
      }
    }
  }

  if (!customElements.get('vt-store-selector')) customElements.define('vt-store-selector', VTStoreSelectorElement);
  if (!customElements.get('vt-buy-online')) customElements.define('vt-buy-online', VTBuyOnlineElement);
})();
